import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "../hooks/auth/use-auth";
import GradientCanvas from "../components/gradient-canvas";
import Loader from "../components/loader";
import { Loader2 } from "lucide-react";
import useCustomTranslation from "../hooks/use-custom-translation";
import Logo from "../components/logo";
import { Link } from "react-router-dom";
import { useContact } from "@/hooks/use-contact";
import ReCAPTCHA from "react-google-recaptcha";
import { useUser } from "@/hooks/user/use-user";

const contactFormSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  surname: z.string().min(1, "Surname is required"),
  email: z.string().email("Invalid email address"),
  message: z.string().min(10, "Message must be at least 10 characters"),
  captchaToken: z.string().nullable(),
});

type ContactFormData = z.infer<typeof contactFormSchema>;

export default function Contact() {
  const { t } = useCustomTranslation();
  const { user, isLoading: isUserLoading } = useUser();
  const { isAuthenticated, isAuthStatusLoading } = useAuth();
  const { contact, isPendingContact, isErrorContact, isSuccessContact } =
    useContact();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      firstName: "",
      surname: "",
      email: "",
      message: "",
      captchaToken: null,
    },
  });

  useEffect(() => {
    if (user) {
      setValue("firstName", user.firstName || "");
      setValue("surname", user.lastName || "");
      setValue("email", user.email || "");
    }
  }, [user, setValue]);

  const onSubmit = async (data: ContactFormData) => {
    if (!data.captchaToken || data.captchaToken === null) {
      alert(t("contact.captchaRequired"));

      return;
    }

    contact({ ...data, captchaToken: data.captchaToken as string });
  };

  const handleCaptchaChange = (token: string | null) => {
    setValue("captchaToken", token);
  };

  if (isAuthStatusLoading || isUserLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center relative overflow-hidden">
        <Loader />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center relative overflow-hidden">
      <GradientCanvas />
      <Card className="my-6 w-4/5 lg:w-3/5 z-10">
        <CardHeader className="space-y-1">
          <Link to="/" className="w-full flex justify-center">
            <Logo />
          </Link>
          <CardTitle className="text-2xl text-center">
            {t("contact.contactUs")}
          </CardTitle>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Champs du formulaire */}
            <div className="space-y-2">
              <Label htmlFor="firstName">{t("contact.firstName")}</Label>
              {/* @ts-expect-error - React Hook Form ref type mismatch */}
              <Input
                id="firstName"
                {...register("firstName")}
                readOnly={isAuthenticated}
                className={`${isAuthenticated ? "bg-gray-100" : ""} ${
                  errors.firstName ? "border-red-500" : ""
                }`}
              />
              {errors.firstName && (
                <p className="text-red-500 text-sm">
                  {errors.firstName.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="surname">{t("contact.surname")}</Label>
              {/* @ts-expect-error - React Hook Form ref type mismatch */}
              <Input
                id="surname"
                {...register("surname")}
                readOnly={isAuthenticated}
                className={`${isAuthenticated ? "bg-gray-100" : ""} ${
                  errors.surname ? "border-red-500" : ""
                }`}
              />
              {errors.surname && (
                <p className="text-red-500 text-sm">{errors.surname.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              {/* @ts-expect-error - React Hook Form ref type mismatch */}
              <Input
                id="email"
                type="email"
                {...register("email")}
                readOnly={isAuthenticated}
                className={`${isAuthenticated ? "bg-gray-100" : ""} ${
                  errors.email ? "border-red-500" : ""
                }`}
              />
              {errors.email && (
                <p className="text-red-500 text-sm">{errors.email.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">{t("contact.message")}</Label>
              {/* @ts-expect-error - React Hook Form ref type mismatch */}
              <Textarea
                id="message"
                {...register("message")}
                className={errors.message ? "border-red-500" : ""}
                placeholder={t("contact.messagePlaceholder")}
              />
              {errors.message && (
                <p className="text-red-500 text-sm">{errors.message.message}</p>
              )}
            </div>

            <div className="flex justify-center">
              <ReCAPTCHA
                sitekey={import.meta.env.VITE_SITE_KEY}
                onChange={handleCaptchaChange}
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-linear-to-b from-green-700 to-gray-700"
              disabled={isPendingContact}
            >
              {isPendingContact ? (
                <div className="flex items-center justify-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t("contact.sending")}
                </div>
              ) : (
                t("contact.sendMessage")
              )}
            </Button>
          </form>

          {isErrorContact && (
            <div className="mt-4 p-3 text-sm text-red-500 bg-red-100 rounded-md text-center">
              {t("contact.genericError")}
            </div>
          )}

          {isSuccessContact && (
            <div className="mt-4 p-3 text-sm text-green-500 bg-green-100 rounded-md text-center">
              {t("contact.genericSuccess")}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
